import * as React from 'react';
import commonStrings from 'owa-strings/owa-mini-common-strings';
import handleArrowKeyPress from 'mini-accessibility/lib/utils/handleKeyArrowPress';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import safeOpenExternalLink from 'window-utils/lib/safeOpenExternalLink';
import { clearSessionStorage } from 'mini-session-storage';
import { default as disableWebPushNotifications } from 'mini-webpush-notifications/lib/disableWebPushNotifications';
import { enterKeyHandler } from 'window-utils/lib/keyHandlerUtils';
import { getApp } from 'owa-config/lib/bootstrapOptions';
import { getNotificationPermission } from 'mini-webpush-notifications/lib/notificationPermissions';
import { isIOSPWA } from 'mini-device-utils/lib/utils/isPWA';
import { isMiniFeatureEnabled } from 'owa-mini-feature-flags';
import { isWebPushNotificationSupported } from 'mini-webpush-notifications';
import { lazyLaunchFeedback } from 'mini-ocv-feedback';
import { logUsage } from 'owa-analytics';
import { MiniFeatures } from 'owa-mini-feature-flags/lib/isMiniFeatureEnabled';
import { MouseEvent } from 'react';
import { observer } from 'mobx-react';
import { OCV_FEEDBACK_LAUNCH_BEGIN } from 'mini-ocv-feedback/lib/utils/eventSignalConstants';
import { shouldShowFeedbackLink } from '../utils/shouldShowFeedbackLink';
import { showFullScreenSpinner } from 'mini-full-screen-spinner';
import { signalWorkflowEvent } from 'mini-workflow-manager';
import {
    arrowLocation,
    handleArrowKeyPressConfig,
} from 'mini-accessibility/lib/schema/handleKeyArrowPressDeclarations';

const styles = require('./SidePaneFooter.scss');

const SIGNOUT_URL = '/owa/logoff.owa';
const GET_HELP_URL = 'https://support.microsoft.com/topic/c8c9be71-02ef-434c-b642-443fb20c1bf9';
const CONSUMER_USER_VOICE_URL =
    'https://outlook.uservoice.com/forums/910579-outlook-com-mobile-webpage-beta';
const ENTERPRISE_USER_VOICE_URL = 'https://outlook.uservoice.com/forums/925936';

export interface SidePaneFooterProps {
    onFeedbackClick: () => void;
    onReportErrorClick: () => void;
    onSidePaneClose: () => void;
    itemRefs?: React.RefObject<any>[];
    startIndex?: number;
    totalLength?: number;
    folder2ExpandedState?: boolean;
}

const handleFocusChange = () => {
    /**
     * Remove the event listener and reload the page
     */
    window.removeEventListener('focus', handleFocusChange);
    setTimeout(() => {
        location.reload();
    }, 0);
};

/**
 * Opens the signout url
 */
const signout = () => {
    if (isIOSPWA() && !isConsumer()) {
        /**
         * In iOS PWA, to allow enterprise user to come back to the PWA page after logging out,
         * open in new tab and then once the user clicks the Done refresh the page
         * using the focus event handler.
         */
        window.open(SIGNOUT_URL, '_blank');
        window.addEventListener('focus', handleFocusChange);
    } else {
        location.assign(SIGNOUT_URL);
    }
};

const onSignout = async () => {
    logUsage('UserSignOut');
    //disable push notifications
    if (isWebPushNotificationSupported(window) && getNotificationPermission(window) === 'granted') {
        await disableWebPushNotifications(window);
    }
    clearSessionStorage(); // Clear out sessionStorage data when user signs out
    signout();
};

export const launchFeedback = (
    onFeedbackClick: (evt?: MouseEvent<HTMLDivElement>) => void | Promise<void>,
    onSidePaneClose?: () => void
): void => {
    /**
     * inapp feedback for sdf/msit
     */
    const isDogFoodFeedback = isMiniFeatureEnabled('mini-feedbackDF');
    /**
     * in ww,
     * mini-ocv-feedback : ON=>OCV
     * OFF: user voice
     */
    const isOCVFeedbackEnabled = isMiniFeatureEnabled('mini-ocv-feedback' as MiniFeatures);
    if (isDogFoodFeedback) {
        onFeedbackClick();
    } else {
        if (isOCVFeedbackEnabled) {
            onSidePaneClose?.();
            /**
             * Show FullScreenSpinner and hide once the lazyLoad is complete
             * from launchFeedback
             */
            showFullScreenSpinner();
            signalWorkflowEvent(OCV_FEEDBACK_LAUNCH_BEGIN);
            lazyLaunchFeedback.importAndExecute();
        } else {
            const userVoiceUrl = isConsumer() ? CONSUMER_USER_VOICE_URL : ENTERPRISE_USER_VOICE_URL;
            logUsage('UserVoiceFeedbackClick');
            window.open(userVoiceUrl);
        }
    }
};

/**
 * This component is to add footer in side pane of mail/calendar/people
 */
function SidePaneFooter(props: SidePaneFooterProps) {
    const {
        onFeedbackClick,
        onReportErrorClick,
        onSidePaneClose,
        itemRefs,
        startIndex,
        totalLength,
    } = props;

    const isDogFoodFeedback = isMiniFeatureEnabled('mini-feedbackDF');
    // we don't want to show feedback link for enterprise users in World Wide ring
    // this should be changed back once OCPS flow works for Enterprise
    const showFeedbackLink = shouldShowFeedbackLink();
    const showReportErrorLink = isDogFoodFeedback && !!onReportErrorClick;

    const handleGetHelpClick = () => {
        onSidePaneClose();
        logUsage('GetHelp', { App: getApp() });
        safeOpenExternalLink(GET_HELP_URL);
    };

    const configSetter = index => {
        let updatedKeyConfig = {
            itemIndex: startIndex,
            itemRefs: itemRefs,
            length: totalLength,
            configUp: arrowLocation.MailFooter,
        };
        updatedKeyConfig.itemIndex += index;
        return updatedKeyConfig;
    };

    const keyConfig: handleArrowKeyPressConfig = {
        itemIndex: startIndex,
        itemRefs: itemRefs,
        length: totalLength,
        configUp: arrowLocation.MailFooter,
    };

    return (
        <div className={styles.sidePaneFooter}>
            <div
                tabIndex={0}
                role="button"
                className={styles.sidePaneFooterItem}
                onKeyUp={enterKeyHandler(handleGetHelpClick)}
                onClick={handleGetHelpClick}
                ref={itemRefs[startIndex]}
                onKeyDown={handleArrowKeyPress(keyConfig)}>
                {commonStrings.getHelpLabel}
            </div>
            {showFeedbackLink && (
                <div
                    tabIndex={0}
                    role="button"
                    className={styles.sidePaneFooterItem}
                    onKeyUp={enterKeyHandler(() =>
                        launchFeedback(onFeedbackClick, onSidePaneClose)
                    )}
                    onClick={() => launchFeedback(onFeedbackClick, onSidePaneClose)}
                    ref={itemRefs[startIndex + 1]}
                    onKeyDown={handleArrowKeyPress(configSetter(1))}>
                    {commonStrings.feedback_Label}
                </div>
            )}
            {showReportErrorLink && (
                <div
                    tabIndex={0}
                    role="button"
                    className={styles.sidePaneFooterItem}
                    onKeyUp={enterKeyHandler(onReportErrorClick)}
                    onClick={onReportErrorClick}
                    ref={showFeedbackLink ? itemRefs[startIndex + 2] : itemRefs[startIndex + 1]}
                    onKeyDown={
                        showFeedbackLink
                            ? handleArrowKeyPress(configSetter(2))
                            : handleArrowKeyPress(configSetter(1))
                    }>
                    {commonStrings.reportErrorButtonLabel}
                </div>
            )}
            <div
                tabIndex={0}
                role="button"
                className={styles.sidePaneFooterItem}
                onKeyUp={enterKeyHandler(onSignout)}
                onClick={onSignout}
                ref={
                    showFeedbackLink && showReportErrorLink
                        ? itemRefs[startIndex + 3]
                        : showFeedbackLink || showReportErrorLink
                        ? itemRefs[startIndex + 2]
                        : itemRefs[startIndex + 1]
                }
                onKeyDown={
                    showFeedbackLink && showReportErrorLink
                        ? handleArrowKeyPress(configSetter(3))
                        : showFeedbackLink || showReportErrorLink
                        ? handleArrowKeyPress(configSetter(2))
                        : handleArrowKeyPress(configSetter(1))
                }>
                {commonStrings.signOut}
            </div>
        </div>
    );
}

export default observer(SidePaneFooter);
